// src/pages/messenger/DashboardMessengerPage.jsx
// Staff Dashboard — hero + pill-strip + 3-column timeline layout (dashboard_v7).
// LAYOUT / STYLE change only. All data fetching, state, useEffects, handlers,
// the kira_action_complete refetch listener and every endpoint call are
// preserved verbatim from the previous revision.
import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MessengerLayout from "../../layout/MessengerLayout";
import { AuthContext } from "../../context/AuthContext";
import KudosPanel from "../../components/staffdashboard/KudosPanel";
import MoodCheckPanel from "../../components/staffdashboard/MoodCheckPanel";
import { Skeleton, ErrorState } from "../../components/ui/LoadingStates";
import { toast } from "../../components/ui/Toast";
import ErrorBoundary from "../../components/ui/ErrorBoundary";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON     = "#00FF99";
const AMBER    = "#FFC24B";
const BLUE     = "#00BFFF";

/* ────────────────────────────────────────────────
   Helpers
──────────────────────────────────────────────── */
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Greeting uses LOCAL clock — `Date.prototype.getHours()` returns the
// host's local hour, never UTC. Bug-bug-fix: previously "Good evening"
// was returned for everything past 17:00 including the middle of the
// night, which felt wrong at 23:00+.
const greet = () => {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  if (h >= 17 && h < 21) return "Good evening";
  return "Good night";
};

const daysBetween = (a, b) =>
  Math.ceil((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" });

const fmtHeaderDate = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day:     "numeric",
    month:   "long",
  });

const getNextPayday = () => {
  const now = new Date();
  let target = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  if (target < now) target = new Date(now.getFullYear(), now.getMonth() + 2, 0);
  while (target.getDay() === 0 || target.getDay() === 6) {
    target.setDate(target.getDate() - 1);
  }
  return target;
};

const relTime = (iso) => {
  if (!iso) return "";
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60)     return "Just now";
  if (diff < 3600)   return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return fmtDate(iso);
};

// Format decimal hours (e.g. 4.2) → "4:12" for the worked-hours ring.
const fmtHM = (h) => {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}:${String(mm).padStart(2, "0")}`;
};

/* ────────────────────────────────────────────────
   Scoped stylesheet (dashboard_v7)
   Everything is namespaced under .dashv7 so nothing leaks globally.
──────────────────────────────────────────────── */
const STYLES = `
.dashv7{
  --surface:#0d0d0d; --surface-2:#131313;
  --green:#00FF99; --green-dim:rgba(0,255,153,.12); --green-line:rgba(0,255,153,.30);
  --blue:#00BFFF; --amber:#FFC24B;
  --muted:rgba(255,255,255,.55); --faint:rgba(255,255,255,.35); --line:rgba(255,255,255,.10);
  color:#fff;
  background:
    radial-gradient(900px 420px at 90% -10%, rgba(0,255,153,.08), transparent 65%),
    radial-gradient(600px 340px at 0% 110%, rgba(0,255,153,.04), transparent 60%),
    #050505;
  min-height:100vh;
  padding:16px clamp(14px,2.5vw,28px) 28px;
  -webkit-font-smoothing:antialiased;
}

/* hero */
.dashv7 .hero{display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:14px;margin:6px 2px 14px}
.dashv7 .hero h1{font-family:'Space Grotesk','Inter',sans-serif;font-size:clamp(22px,3vw,32px);font-weight:600;letter-spacing:-.03em;margin:0}
.dashv7 .hero .sub{color:var(--muted);font-size:12.5px;margin-top:4px}
.dashv7 .big-stats{display:flex;gap:26px;flex-wrap:wrap}
.dashv7 .big-stat{display:flex;gap:9px;align-items:flex-start}
.dashv7 .big-stat .num{font-family:'Space Grotesk','Inter',sans-serif;font-size:32px;font-weight:600;letter-spacing:-.03em;line-height:1}
.dashv7 .big-stat .num small{font-size:16px;color:var(--muted);font-weight:500}
.dashv7 .big-stat .lbl{font-size:10.5px;color:var(--muted);margin-top:5px;max-width:120px}
.dashv7 .big-stat .chip{width:24px;height:24px;border-radius:8px;background:var(--green-dim);border:1px solid var(--green-line);display:grid;place-items:center;font-size:11px;margin-top:3px;flex:none}
.dashv7 .big-stat.amber .chip{background:rgba(255,194,75,.12);border-color:rgba(255,194,75,.35)}
.dashv7 .big-stat.amber .num{color:var(--amber)}

/* pill strip */
.dashv7 .meters{display:flex;align-items:flex-end;gap:8px;margin:0 2px 16px;flex-wrap:wrap}
.dashv7 .meter{display:flex;flex-direction:column;gap:4px}
.dashv7 .meter .m-lbl{font-size:10.5px;color:var(--muted);font-weight:500}
.dashv7 .pill{height:36px;border-radius:999px;display:flex;align-items:center;padding:0 14px;font-size:11.5px;font-weight:600;gap:8px;white-space:nowrap}
.dashv7 .pill.green{background:var(--green);color:#000}
.dashv7 .pill.outline{background:var(--surface);border:1px solid var(--line);color:#fff}
.dashv7 .pill.hatch{
  width:min(280px,60vw);color:var(--muted);
  background:repeating-linear-gradient(-55deg,transparent 0 5px,rgba(255,255,255,.10) 5px 6px);
  border:1px solid var(--line);position:relative;overflow:hidden;
}
.dashv7 .pill.hatch .fill{position:absolute;inset:0;background:linear-gradient(90deg,rgba(0,255,153,.25),rgba(0,255,153,.10));border-right:2px solid var(--green)}
.dashv7 .pill.hatch span{position:relative}

/* 3-column main */
.dashv7 .main{display:grid;grid-template-columns:250px minmax(0,1fr) 290px;gap:12px;align-items:start}
.dashv7 .rail{display:flex;flex-direction:column;gap:12px;position:sticky;top:12px}

.dashv7 .card{background:var(--surface);border:1px solid var(--line);border-radius:22px;padding:16px;position:relative}
.dashv7 .card h3{font-size:13px;font-weight:600;letter-spacing:-.01em;margin:0}

/* your status */
.dashv7 .s-row{border-top:1px dashed var(--line);padding:9px 0 8px}
.dashv7 .s-row:first-of-type{border-top:none;margin-top:4px;padding-top:6px}
.dashv7 .s-row .k{font-size:10.5px;color:var(--muted)}
.dashv7 .s-row .v{font-size:13px;font-weight:600;margin-top:2px;display:flex;justify-content:space-between;align-items:center}
.dashv7 .s-row .v em{font-style:normal;font-size:10.5px;color:var(--faint);font-weight:500}
.dashv7 .bar{height:5px;border-radius:99px;background:rgba(255,255,255,.08);margin-top:6px;overflow:hidden}
.dashv7 .bar i{display:block;height:100%;background:var(--green);border-radius:99px}
.dashv7 .s-ok{color:var(--green)} .dashv7 .s-warn{color:var(--amber)}

/* quick actions */
.dashv7 .qa{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:10px}
.dashv7 .qa button{
  background:var(--surface-2);border:1px solid var(--line);border-radius:13px;color:#fff;
  font-size:11px;font-weight:600;padding:10px 8px;cursor:pointer;
  display:flex;flex-direction:column;gap:5px;align-items:flex-start;font-family:'Inter',sans-serif;text-align:left;
}
.dashv7 .qa button:hover{border-color:var(--green-line)}
.dashv7 .qa .ic{width:22px;height:22px;border-radius:7px;background:var(--green-dim);border:1px solid var(--green-line);display:grid;place-items:center;font-size:10px}

/* time ring */
.dashv7 .ring-wrap{display:flex;align-items:center;justify-content:center;gap:14px;padding:8px 0 2px}
.dashv7 .ring{
  width:96px;height:96px;border-radius:50%;flex:none;
  display:grid;place-items:center;position:relative;
  filter:drop-shadow(0 0 12px rgba(0,255,153,.2));
}
.dashv7 .ring::before{content:"";position:absolute;inset:9px;background:var(--surface);border-radius:50%}
.dashv7 .ring .t{position:relative;text-align:center}
.dashv7 .ring .t .h{font-family:'Space Grotesk','Inter',sans-serif;font-size:19px;font-weight:600;letter-spacing:-.02em}
.dashv7 .ring .t .s{font-size:8.5px;color:var(--muted)}
.dashv7 .btn{border:none;border-radius:999px;padding:8px 14px;font-size:11.5px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif}
.dashv7 .btn.green{background:var(--green);color:#000}
.dashv7 .btn.green:hover{box-shadow:0 0 14px rgba(0,255,153,.35)}
.dashv7 .btn.ghost{background:transparent;border:1px solid var(--line);color:#fff}
.dashv7 .btn.ghost:hover{border-color:var(--green-line);color:var(--green)}

/* feed timeline */
.dashv7 .feed{display:flex;flex-direction:column;gap:12px;position:relative;padding-left:24px}
.dashv7 .feed::before{
  content:"";position:absolute;left:7px;top:14px;bottom:14px;width:2px;
  background:linear-gradient(180deg,var(--green-line),rgba(255,255,255,.08));
}
.dashv7 .feed-item{position:relative}
.dashv7 .fdot{
  position:absolute;left:-22px;top:20px;width:10px;height:10px;border-radius:99px;
  background:var(--surface);border:2px solid var(--green);box-shadow:0 0 8px rgba(0,255,153,.4);
  cursor:pointer;z-index:2;transition:.15s;
}
.dashv7 .fdot:hover{transform:scale(1.35)}
.dashv7 .feed-item.dim .fdot{border-color:rgba(255,255,255,.25);box-shadow:none}
.dashv7 .feed-item.closed .fdot{background:var(--surface);border-color:rgba(255,255,255,.35);box-shadow:none}
.dashv7 .feed-item.closed .card > :not(.f-head){display:none}
.dashv7 .feed-item .f-head{cursor:pointer;user-select:none}
.dashv7 .f-head .chev{font-size:9px;color:var(--faint);margin-left:6px;display:inline-block;transition:.15s}
.dashv7 .feed-item.closed .chev{transform:rotate(-90deg)}
.dashv7 .feed-item.closed .f-head{margin-bottom:0}
.dashv7 .f-head{display:flex;align-items:center;gap:8px;margin-bottom:9px}
.dashv7 .f-head .t{font-size:13px;font-weight:600}
.dashv7 .f-head .when{font-size:10.5px;color:var(--faint);margin-left:auto}

/* list rows */
.dashv7 .rows{display:flex;flex-direction:column;gap:6px;margin-top:9px}
.dashv7 .row-item{display:flex;align-items:center;gap:10px;background:var(--surface-2);border:1px solid var(--line);border-radius:12px;padding:8px 10px;font-size:12px;cursor:pointer}
.dashv7 .row-item:hover{border-color:var(--green-line)}
.dashv7 .row-item .ic{width:26px;height:26px;border-radius:9px;display:grid;place-items:center;font-size:12px;flex:none;background:var(--green-dim);border:1px solid var(--green-line)}
.dashv7 .row-item .ic.b{background:rgba(0,191,255,.10);border-color:rgba(0,191,255,.30)}
.dashv7 .row-item .ic.a{background:rgba(255,194,75,.10);border-color:rgba(255,194,75,.30)}
.dashv7 .row-item .ic.v{background:rgba(127,119,221,.12);border-color:rgba(127,119,221,.35)}
.dashv7 .row-item .rt{font-weight:600;line-height:1.3;min-width:0}
.dashv7 .row-item .rt .d{font-size:10.5px;color:var(--faint);margin-top:1px;font-weight:500}
.dashv7 .row-item .end{margin-left:auto;font-size:10px;color:var(--faint);white-space:nowrap}
.dashv7 .stag{font-size:9.5px;font-weight:600;padding:2px 8px;border-radius:99px;margin-left:auto;white-space:nowrap}
/* fixed modifier classes — never derived from status strings (avoids the .open collision) */
.dashv7 .stag.tg-open{background:var(--green-dim);color:var(--green);border:1px solid var(--green-line)}
.dashv7 .stag.tg-prog{background:rgba(0,191,255,.10);color:var(--blue);border:1px solid rgba(0,191,255,.30)}
.dashv7 .stag.tg-muted{background:rgba(255,255,255,.05);color:var(--faint);border:1px solid var(--line)}
.dashv7 .see-all{font-size:10.5px;color:var(--green);font-weight:600;text-decoration:none;display:inline-block;margin-top:8px;cursor:pointer}
.dashv7 .empty{font-size:12px;color:var(--faint);margin:0}

/* responsive */
@media (max-width:1100px){
  .dashv7 .main{grid-template-columns:minmax(0,1fr) 280px}
  .dashv7 .rail.left{position:static;grid-column:1/-1;display:grid;grid-template-columns:1fr 1fr;gap:12px}
}
@media (max-width:720px){
  .dashv7{padding:12px 12px 20px}
  .dashv7 .main{grid-template-columns:1fr}
  .dashv7 .rail{position:static}
  .dashv7 .rail.left{grid-template-columns:1fr}
  .dashv7 .rail.right{order:3}
  .dashv7 .feed-col{order:2}
  .dashv7 .hero{align-items:flex-start;flex-direction:column}
  .dashv7 .pill.hatch{width:100%}
}
`;

/* ════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════ */
export default function DashboardMessengerPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // ── State (UNCHANGED) ──
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [attendance,   setAttendance]   = useState(null);
  const [tickets,      setTickets]      = useState([]);
  const [holidays,     setHolidays]     = useState([]);
  const [birthdays,    setBirthdays]    = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [goals,         setGoals]         = useState([]);
  const [monthStats,    setMonthStats]    = useState(null);

  const [loading, setLoading] = useState({
    leave: true, attendance: true, tickets: true,
    holidays: true, birthdays: true, announcements: true,
    goals: true, monthStats: true,
  });
  const [goalsModalOpen, setGoalsModalOpen] = useState(false);
  // B4: per-user collapsed feed sections, persisted in localStorage
  // (same key pattern as the onboarding keys — kwery_*_{uid}). Default: all expanded.
  const [collapsed, setCollapsed] = useState({});
  const [errors, setErrors] = useState({
    leave: null, attendance: null, tickets: null,
    holidays: null, birthdays: null, announcements: null,
    goals: null, monthStats: null,
  });

  // ── Auth guard (UNCHANGED) ──
  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  const loadSection = useCallback((key, url, transform) => {
    setLoading((l) => ({ ...l, [key]: true }));
    setErrors((e) => ({ ...e, [key]: null }));
    fetch(`${API_BASE}${url}`, { headers: authHeaders() })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => transform(d))
      .catch((err) => setErrors((e) => ({ ...e, [key]: err.message || "Failed to load" })))
      .finally(() => setLoading((l) => ({ ...l, [key]: false })));
  }, []);

  const refetch = useCallback(() => {
    loadSection("leave", "/api/leave/balance", (d) => { if (d) setLeaveBalance(d); });
    const today = new Date().toISOString().split("T")[0];
    loadSection("attendance", `/api/attendance/stats?start=${today}&end=${today}`, (d) => {
      if (d) setAttendance({
        check_in:  d.check_in  || d.first_in  || null,
        check_out: d.check_out || d.last_out  || null,
        hours:     d.total_hours || d.hours   || null,
        status:    d.present_days > 0 ? "present" : "not_in",
      });
    });
    loadSection("tickets", "/api/tickets/list", (d) => {
      const open = (Array.isArray(d) ? d : []).filter(
        (t) => t.status !== "Closed" && t.status !== "Resolved"
      );
      setTickets(open.slice(0, 5));   // B4: dashboard slice bumped 4 → 5
    });
    loadSection("holidays", "/api/holidays/upcoming", (d) => {
      setHolidays(Array.isArray(d) ? d.slice(0, 4) : []);
    });
    loadSection("birthdays", "/api/team/celebrations", (d) => {
      setBirthdays(Array.isArray(d) ? d.slice(0, 5) : []);   // B4: 4 → 5
    });
    loadSection("announcements", "/api/announcements/list", (d) => {
      setAnnouncements(Array.isArray(d) ? d.slice(0, 5) : []);   // B4: 3 → 5
    });
    // Goals (for the "Goal progress" KPI) — response is either an array
    // or `{goals: [...]}` depending on endpoint version.
    loadSection("goals", "/api/performance/goals", (d) => {
      const list = Array.isArray(d) ? d : (d?.goals || []);
      setGoals(list);
    });
    // Month-to-date attendance (for the "Late arrivals" sidebar row).
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString().split("T")[0];
    const todayIso   = now.toISOString().split("T")[0];
    loadSection(
      "monthStats",
      `/api/attendance/stats?start=${monthStart}&end=${todayIso}`,
      (d) => { if (d) setMonthStats(d); }
    );
  }, [loadSection]);

  // ── Fetch all data ──
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    refetch();
    const handleKiraAction = () => refetch();
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () =>
      window.removeEventListener("kira_action_complete", handleKiraAction);
  }, [user, refetch]);

  // ── B4: load persisted collapsed state for this user ──
  const uid = user?.id || "anon";
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`kwery_dash_collapsed_${uid}`);
      setCollapsed(raw ? JSON.parse(raw) : {});
    } catch {
      setCollapsed({});
    }
  }, [uid]);

  const toggleSection = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(`kwery_dash_collapsed_${uid}`, JSON.stringify(next));
      } catch {
        /* ignore quota / private-mode errors */
      }
      return next;
    });
  };

  // ── Derived values ──
  const firstName = user?.full_name?.split(" ")[0] || "there";
  const dept      = user?.department || user?.dept || user?.role || "—";
  const nextPayday   = getNextPayday();
  const daysToPayday = daysBetween(new Date(), nextPayday);

  // ── Leave balances (B1 read fix) ──
  // GET /api/leave/balance returns { balances: [{type,total,used}, ...] }.
  // Older revisions read flat annual/annual_used fields the API never sends,
  // so every value showed "—". We now parse balances[]; a legacy flat shape is
  // still honoured as a fallback. Genuinely-empty policies fall to 0 / "no policy".
  const leaveResolved = !loading.leave && !errors.leave;
  const _balArr = Array.isArray(leaveBalance?.balances) ? leaveBalance.balances : [];
  const _findBal = (name) =>
    _balArr.find((b) => String(b.type || "").toLowerCase() === name);
  const _annualBal = _findBal("annual");
  const _sickBal   = _findBal("sick");

  const annualTotal =
    _annualBal ? Number(_annualBal.total)
    : leaveBalance?.annual != null ? Number(leaveBalance.annual)
    : null;
  const annualUsed =
    _annualBal ? Number(_annualBal.used)
    : leaveBalance?.annual_used != null ? Number(leaveBalance.annual_used)
    : null;
  const hasAnnual = annualTotal != null && annualUsed != null;

  const sickTotal =
    _sickBal ? Number(_sickBal.total)
    : leaveBalance?.sick != null ? Number(leaveBalance.sick)
    : null;
  const sickUsed =
    _sickBal ? Number(_sickBal.used)
    : leaveBalance?.sick_used != null ? Number(leaveBalance.sick_used)
    : null;
  const hasSick = sickTotal != null && sickUsed != null;

  // API resolved but no annual balance at all → no leave policy assigned.
  const noLeavePolicy =
    leaveResolved && !hasAnnual && leaveBalance?.total_remaining == null;

  const annualRemaining = hasAnnual
    ? annualTotal - annualUsed
    : leaveBalance?.total_remaining != null
      ? leaveBalance.total_remaining
      : noLeavePolicy ? 0 : "—";

  const sickRemaining = hasSick
    ? sickTotal - sickUsed
    : noLeavePolicy ? 0 : "—";

  // ── Goal progress (on track / total) ──
  // Counts: COMPLETED + ON_TRACK = green. AT_RISK = amber.
  // Backend status values are upper-case enum names but we lowercase
  // defensively so older payloads still match.
  const goalCounts = (() => {
    if (!Array.isArray(goals) || goals.length === 0) return null;
    let ok = 0, risk = 0, done = 0;
    for (const g of goals) {
      const s = String(g.status || "").toLowerCase();
      if (s === "completed")                 { done += 1; ok += 1; }
      else if (s === "at_risk")              { risk += 1; }
      else                                    { ok += 1; }
    }
    return { ok, risk, done, total: goals.length };
  })();
  const goalProgressLabel = goalCounts
    ? `${goalCounts.ok}/${goalCounts.total}`
    : "—";

  // ── Late arrivals this month ──
  // `/api/attendance/stats` returns `late_flags` for the requested
  // window — labelled "Late Flags" in the stats card.
  const lateThisMonth = (monthStats && monthStats.late_flags != null)
    ? `${monthStats.late_flags} this month`
    : "— this month";

  // ── Clock in/out (logic UNCHANGED — reusable handler) ──
  const handleClock = async () => {
    const action = attendance?.check_in && !attendance?.check_out ? "out" : "in";
    try {
      const res = await fetch(`${API_BASE}/api/attendance/clock`, {
        method:  "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body:    JSON.stringify({ action }),
      });
      if (res.ok) {
        const d = await res.json();
        setAttendance(d);
        toast.success(`Clocked ${action}`);
        // KIRA diary end-of-day signal (S3): clock-out is the reliable
        // wind-down trigger. Fire-and-forget; the evening check-in listens.
        if (action === "out") {
          window.dispatchEvent(new CustomEvent("diary_end_of_day", { detail: { source: "clock_out" } }));
        }
      } else {
        const legacy = await fetch(
          `${API_BASE}/api/attendance/${action === "in" ? "checkin" : "checkout"}`,
          { method: "POST", headers: authHeaders() }
        );
        if (legacy.ok) {
          const d = await legacy.json();
          setAttendance(d);
          toast.success(`Clocked ${action}`);
          if (action === "out") {
            window.dispatchEvent(new CustomEvent("diary_end_of_day", { detail: { source: "clock_out" } }));
          }
        } else {
          toast.error(`Clock ${action} failed. Please try from the attendance page.`);
        }
      }
    } catch (err) {
      console.error("Clock error:", err);
      toast.error("Could not connect. Check your network.");
    }
  };

  // ── Presentational derivations (no data logic) ──
  const clockLabel = attendance?.check_in && !attendance?.check_out ? "Clock out" : "Clock in";
  const clockIcon  = attendance?.check_in && !attendance?.check_out ? "⏹" : "⏱";

  const workedHours = Number(attendance?.hours);
  const hasHours    = Number.isFinite(workedHours) && workedHours > 0;
  const ringPct     = hasHours ? Math.max(0, Math.min(100, (workedHours / 8) * 100)) : 0;
  const workedLabel = hasHours ? fmtHM(workedHours) : "0:00";

  // annual-leave hatched pill + status bars (percentages, from the parsed balances)
  const hasLeaveBar = hasAnnual && annualTotal > 0;
  const usedPct     = hasLeaveBar ? Math.round((annualUsed / annualTotal) * 100) : 0;
  const annualRemPct =
    hasLeaveBar ? Math.max(0, Math.min(100, ((annualTotal - annualUsed) / annualTotal) * 100)) : 0;

  const hasSickBar = hasSick && sickTotal > 0;
  const sickRemPct = hasSickBar
    ? Math.max(0, Math.min(100, ((sickTotal - sickUsed) / sickTotal) * 100))
    : 0;

  // B2: single shared source for BOTH the header stat and the pill-strip pill,
  // so they can never diverge. (Both already read `tickets`; this makes it explicit.)
  const openTicketCount = tickets.length;

  /* ════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════ */
  return (
    <MessengerLayout>
      {/* Right padding (pr-4 md:pr-[72px]) preserved: the global KIRA float
          stays mounted app-wide, anchored bottom-right, so the feed keeps a
          safe gutter beneath it. */}
      <style>{STYLES}</style>
      <div className="dashv7 text-white w-full pr-4 md:pr-[72px]">

        {/* ═══ HERO — greeting + inline big-number stats ═══ */}
        <div className="hero">
          <div style={{ minWidth: 0 }}>
            <h1>{greet()}, {firstName}</h1>
            <div className="sub">{fmtHeaderDate()} · {dept}</div>
          </div>

          <div className="big-stats">
            {loading.leave
              ? <BigStatSkeleton />
              : <BigStat
                  chip="🌴"
                  value={hasAnnual ? annualRemaining : 0}
                  label={noLeavePolicy ? "No leave policy" : "Leave days remaining"}
                />}
            {loading.tickets
              ? <BigStatSkeleton />
              : <BigStat chip="🎫" value={openTicketCount} label="Open tickets" />}
            {loading.goals
              ? <BigStatSkeleton />
              : <BigStat
                  chip="◎"
                  value={goalProgressLabel}
                  amber={!!(goalCounts && goalCounts.risk > 0)}
                  label={goalCounts && goalCounts.risk > 0
                    ? `Goals on track · ${goalCounts.risk} at risk`
                    : "Goals on track"}
                />}
            <BigStat chip="💷" value={daysToPayday} label="Days to payday" />
          </div>
        </div>

        {/* ═══ PILL STRIP ═══ */}
        <div className="meters">
          {/* (a) checked-in */}
          <div className="meter">
            <span className="m-lbl">Attendance</span>
            {attendance?.check_in ? (
              <div className="pill green">● Checked in · {attendance.check_in}</div>
            ) : (
              <div className="pill outline">Not checked in</div>
            )}
          </div>

          {/* (b) mood — inline emoji pill (existing component, ErrorBoundary kept) */}
          <div className="meter">
            <span className="m-lbl">How are you feeling?</span>
            <ErrorBoundary variant="panel" label="Mood check"><MoodCheckPanel /></ErrorBoundary>
          </div>

          {/* (c) open tickets — same source as the header stat (B2) */}
          {openTicketCount > 0 && (
            <div className="meter">
              <span className="m-lbl">Tickets</span>
              <div className="pill outline">{openTicketCount} open</div>
            </div>
          )}

          {/* (d) annual leave hatched progress — renders once leave resolves,
                 at 0% fill when there's no policy (B1) */}
          {leaveResolved && (
            <div className="meter">
              <span className="m-lbl">Annual leave used · {usedPct}%</span>
              <div className="pill hatch">
                <div className="fill" style={{ width: `${usedPct}%` }} />
                <span>{hasAnnual ? `${annualUsed} of ${annualTotal} days` : "No leave policy"}</span>
              </div>
            </div>
          )}
        </div>

        {/* ═══ MAIN — left rail · feed · right rail ═══ */}
        <div className="main">

          {/* ── LEFT RAIL ── */}
          <div className="rail left">
            {/* Your status */}
            <div className="card">
              <h3>Your status</h3>

              <div className="s-row">
                <div className="k">Annual leave</div>
                {hasAnnual ? (
                  <>
                    <div className="v">
                      <span>{annualRemaining} days left</span>
                      <em>of {annualTotal}</em>
                    </div>
                    <div className="bar"><i style={{ width: `${annualRemPct}%` }} /></div>
                  </>
                ) : (
                  <div className="v">
                    <span className="s-warn">
                      {noLeavePolicy ? "No leave policy assigned" : (leaveResolved ? "0 days" : "—")}
                    </span>
                  </div>
                )}
              </div>

              <div className="s-row">
                <div className="k">Sick leave</div>
                {hasSick ? (
                  <>
                    <div className="v">
                      <span>{sickRemaining} days left</span>
                      <em>of {sickTotal}</em>
                    </div>
                    <div className="bar"><i style={{ width: `${sickRemPct}%` }} /></div>
                  </>
                ) : (
                  <div className="v">
                    <span>{leaveResolved ? "0 days" : "—"}</span>
                  </div>
                )}
              </div>

              <div className="s-row">
                <div className="k">Attendance today</div>
                <div className="v">
                  <span className={attendance?.check_in ? "s-ok" : "s-warn"}>
                    {attendance?.check_in ? "Present" : "Not checked in"}
                  </span>
                  {attendance?.check_in && <em>since {attendance.check_in}</em>}
                </div>
              </div>

              <div className="s-row">
                <div className="k">Late arrivals this month</div>
                <div className="v">
                  <span className={monthStats && (monthStats.late_flags || 0) > 0 ? "s-warn" : ""}>
                    {lateThisMonth}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="card">
              <h3>Quick actions</h3>
              <div className="qa">
                <button onClick={() => navigate("/messenger/leave?apply=true")}>
                  <span className="ic">🌴</span>Apply for leave
                </button>
                <button onClick={handleClock}>
                  <span className="ic">{clockIcon}</span>{clockLabel}
                </button>
                <button onClick={() => navigate("/messenger/tickets?new=true")}>
                  <span className="ic">🎫</span>Raise a ticket
                </button>
                <button onClick={() => setGoalsModalOpen(true)}>
                  <span className="ic">◎</span>Update goal
                </button>
              </div>
            </div>
          </div>

          {/* ── FEED ── */}
          <div className="feed feed-col">

            {/* Announcements — collapsible; compact dashboard-local rows (B3) */}
            <div className={`feed-item${collapsed.announcements ? " closed" : ""}`}>
              <span className="fdot" onClick={() => toggleSection("announcements")} />
              <div className="card">
                <div className="f-head" onClick={() => toggleSection("announcements")}>
                  <span className="t">📣 Announcements<span className="chev">▼</span></span>
                  <span className="when">latest 5</span>
                </div>
                {loading.announcements ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Skeleton width="60%" height={12} />
                    <Skeleton width="90%" height={10} />
                    <Skeleton width="80%" height={10} />
                  </div>
                ) : (errors.announcements || announcements.length === 0) ? (
                  <p className="empty">No announcements yet</p>
                ) : (
                  <>
                    <div className="rows" style={{ marginTop: 0 }}>
                      {announcements.slice(0, 5).map((a) => {
                        const icon = a.category === "kudos" ? "💚"
                                   : a.category === "urgent" ? "⚠️" : "📣";
                        const chip = a.category === "urgent" ? "a" : "b";
                        return (
                          <div
                            className="row-item"
                            key={a.id}
                            onClick={() => navigate("/messenger/announcements")}
                          >
                            <span className={`ic ${chip}`}>{icon}</span>
                            <div className="rt">
                              <div>{a.title}</div>
                              <div className="d">
                                {(a.author_name || "Admin")}{a.created_at ? ` · ${relTime(a.created_at)}` : ""}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <a className="see-all" href="/messenger/announcements">See all announcements →</a>
                  </>
                )}
              </div>
            </div>

            {/* Team celebrations — collapsible */}
            <div className={`feed-item${collapsed.celebrations ? " closed" : ""}`}>
              <span className="fdot" onClick={() => toggleSection("celebrations")} />
              <div className="card">
                <div className="f-head" onClick={() => toggleSection("celebrations")}>
                  <span className="t">🎉 Team celebrations<span className="chev">▼</span></span>
                  <span className="when">this week</span>
                </div>
                {loading.birthdays ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Skeleton width="50%" height={12} />
                    <Skeleton width="70%" height={10} />
                  </div>
                ) : errors.birthdays ? (
                  <ErrorState message="Couldn't load celebrations" onRetry={refetch} />
                ) : birthdays.length === 0 ? (
                  <p className="empty">No celebrations this week</p>
                ) : (
                  <div className="rows" style={{ marginTop: 0 }}>
                    {birthdays.slice(0, 5).map((p, i) => {
                      const name = p.name || p.full_name || "Someone";
                      const type = p.type === "anniversary"
                        ? `${p.years || ""}${p.years ? "-year " : ""}anniversary`
                        : "Birthday";
                      return (
                        <div className="row-item" key={p.id || i} style={{ cursor: "default" }}>
                          <span className="ic v">{p.type === "anniversary" ? "🏅" : "🎂"}</span>
                          <div className="rt">
                            <div>{name} · {type}</div>
                            <div className="d">{p.message || (p.department || p.dept || "")}</div>
                          </div>
                          {p.date && <span className="end">{fmtDate(p.date)}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <a className="see-all" href="/messenger/announcements">Send wishes →</a>
              </div>
            </div>

            {/* Ticket updates — collapsible */}
            <div className={`feed-item dim${collapsed.tickets ? " closed" : ""}`}>
              <span className="fdot" onClick={() => toggleSection("tickets")} />
              <div className="card">
                <div className="f-head" onClick={() => toggleSection("tickets")}>
                  <span className="t">🎫 Ticket updates<span className="chev">▼</span></span>
                  <span className="when">{openTicketCount} open</span>
                </div>
                {loading.tickets ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Skeleton width="55%" height={12} />
                    <Skeleton width="75%" height={10} />
                  </div>
                ) : errors.tickets ? (
                  <ErrorState message="Couldn't load tickets" onRetry={refetch} />
                ) : tickets.length === 0 ? (
                  <p className="empty">No open tickets</p>
                ) : (
                  <div className="rows" style={{ marginTop: 0 }}>
                    {tickets.slice(0, 5).map((t, i) => {
                      const deptLabel = t.department || t.category || "IT";
                      const tag = ticketTag(t.status);
                      return (
                        <div
                          className="row-item"
                          key={t.id || i}
                          onClick={() => navigate("/messenger/tickets", { state: { ticketId: t.id } })}
                        >
                          <span className="ic">🎫</span>
                          <div className="rt">
                            <div>{t.subject || t.title || "Ticket"}</div>
                            <div className="d">
                              {deptLabel}{t.created_at ? ` · ${fmtDate(t.created_at)}` : ""}
                            </div>
                          </div>
                          <span className={`stag ${tag.cls}`}>{tag.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <a className="see-all" href="/messenger/tickets">All tickets →</a>
              </div>
            </div>

            {/* Kudos — existing component, ErrorBoundary kept; collapsible via the
                dot/header. When open the KudosPanel renders its own card (untouched);
                when closed we show a lightweight header stub so nothing is mounted. */}
            <div className={`feed-item dim${collapsed.kudos ? " closed" : ""}`}>
              <span className="fdot" onClick={() => toggleSection("kudos")} />
              {collapsed.kudos ? (
                <div className="card">
                  <div className="f-head" onClick={() => toggleSection("kudos")} style={{ marginBottom: 0 }}>
                    <span className="t">💚 Kudos<span className="chev">▼</span></span>
                    <span className="when">recent</span>
                  </div>
                </div>
              ) : (
                <ErrorBoundary variant="panel" label="Kudos"><KudosPanel /></ErrorBoundary>
              )}
            </div>
          </div>

          {/* ── RIGHT RAIL ── */}
          <div className="rail right">
            {/* Time today */}
            <div className="card">
              <h3>Time today</h3>
              <div className="ring-wrap">
                <div
                  className="ring"
                  style={{
                    background: `conic-gradient(var(--green) 0 ${ringPct}%, rgba(255,255,255,.08) ${ringPct}% 100%)`,
                  }}
                >
                  <div className="t">
                    <div className="h">{workedLabel}</div>
                    <div className="s">of 8:00</div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
                    {attendance?.check_in ? `Checked in ${attendance.check_in}` : "Not checked in yet"}
                  </div>
                  <button className="btn green" onClick={handleClock}>{clockLabel}</button>
                </div>
              </div>
            </div>

            {/* Upcoming */}
            <div className="card">
              <h3>Upcoming</h3>
              {loading.holidays ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 9 }}>
                  <Skeleton width="60%" height={11} />
                  <Skeleton width="40%" height={10} />
                </div>
              ) : errors.holidays ? (
                <ErrorState message="Couldn't load holidays" onRetry={refetch} />
              ) : holidays.length === 0 ? (
                <p className="empty" style={{ marginTop: 9 }}>No upcoming holidays</p>
              ) : (
                <div className="rows">
                  {holidays.slice(0, 3).map((h, i) => {
                    const days = daysBetween(new Date(), h.date);
                    const dayLabel = days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days}d`;
                    return (
                      <div className="row-item" key={h.id || i} style={{ cursor: "default" }}>
                        <span className="ic b">🗓</span>
                        <div className="rt">
                          <div>{h.name}</div>
                          <div className="d">{fmtDate(h.date)}</div>
                        </div>
                        <span className="end">{dayLabel}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {goalsModalOpen && (
        <GoalUpdateModal
          onClose={() => setGoalsModalOpen(false)}
          onUpdated={refetch}
        />
      )}
    </MessengerLayout>
  );
}

/* ════════════════════════════════════════════════
   LOCAL SUB-COMPONENTS
════════════════════════════════════════════════ */

// Ticket status → fixed modifier class (NEVER the raw status string).
function ticketTag(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("progress")) return { label: "In progress", cls: "tg-prog" };
  return { label: status || "Open", cls: "tg-open" };
}

function BigStat({ chip, value, label, amber }) {
  // Split a "4/6"-style value so the denominator renders smaller (spec look).
  let main = value, small = null;
  if (typeof value === "string" && value.includes("/")) {
    const [a, ...rest] = value.split("/");
    main = a;
    small = `/${rest.join("/")}`;
  }
  return (
    <div className={`big-stat${amber ? " amber" : ""}`}>
      <div className="chip">{chip}</div>
      <div>
        <div className="num">{main}{small && <small>{small}</small>}</div>
        <div className="lbl">{label}</div>
      </div>
    </div>
  );
}

function BigStatSkeleton() {
  return (
    <div className="big-stat">
      <div className="chip" />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Skeleton width={40} height={28} />
        <Skeleton width={64} height={9} />
      </div>
    </div>
  );
}

function GoalUpdateModal({ onClose, onUpdated }) {
  const [goals, setGoals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/performance/goals?status=active`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => {
        const list = Array.isArray(d) ? d : (d?.goals || []);
        setGoals(list.filter((g) => String(g.status || "").toLowerCase() !== "completed"));
      })
      .catch(() => setGoals([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (g, newStatus) => {
    setBusyId(g.id);
    try {
      const res = await fetch(`${API_BASE}/api/performance/goals/${g.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Goal updated");
      setGoals((list) => list.map((x) => x.id === g.id ? { ...x, status: newStatus } : x));
      onUpdated?.();
    } catch {
      toast.error("Could not update goal");
    } finally {
      setBusyId(null);
    }
  };

  const STATUSES = [
    { key: "not_started", label: "Not started" },
    { key: "in_progress", label: "In progress" },
    { key: "completed",   label: "Completed" },
    { key: "on_hold",     label: "On hold" },
  ];

  const badgeColor = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "completed")   return NEON;
    if (v === "at_risk" || v === "on_hold") return AMBER;
    if (v === "in_progress") return BLUE;
    return "#666";
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(4px)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0d0d0d", border: `1px solid ${NEON}4d`,
          borderRadius: 20, width: 480, maxWidth: "100%",
          maxHeight: "80vh", display: "flex", flexDirection: "column",
          boxShadow: "0 0 40px rgba(0,255,153,.12)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,.10)",
        }}>
          <div style={{ fontSize: 15, color: "#fff", fontWeight: 600, fontFamily: "'Space Grotesk','Inter',sans-serif" }}>
            Update goals
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "none", color: "#888",
              fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <p style={{ fontSize: 12, color: "#888", margin: 0 }}>Loading…</p>
          ) : goals.length === 0 ? (
            <p style={{ fontSize: 12, color: "#666", margin: 0 }}>No active goals to update.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {goals.map((g) => {
                const color = badgeColor(g.status);
                return (
                  <div
                    key={g.id}
                    style={{
                      background: "#0f0f0f", border: "1px solid rgba(255,255,255,.10)",
                      borderRadius: 12, padding: "12px 14px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, fontSize: 12, color: "#fff", fontWeight: 600 }}>
                        {g.title || g.name || "Untitled goal"}
                      </div>
                      <span style={{
                        fontSize: 10, padding: "2px 8px", borderRadius: 999,
                        color, background: `${color}1a`,
                        border: `1px solid ${color}4d`, textTransform: "capitalize",
                      }}>
                        {String(g.status || "—").replace(/_/g, " ")}
                      </span>
                    </div>
                    <select
                      value=""
                      disabled={busyId === g.id}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) updateStatus(g, v);
                      }}
                      style={{
                        width: "100%", padding: "7px 9px", fontSize: 11,
                        background: "#131313", border: "1px solid rgba(255,255,255,.10)",
                        borderRadius: 999, color: "#fff", outline: "none",
                      }}
                    >
                      <option value="">Update status…</option>
                      {STATUSES.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 6,
          padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,.10)",
        }}>
          <button
            onClick={onClose}
            style={{
              border: "1px solid rgba(255,255,255,.10)", background: "transparent",
              borderRadius: 999, padding: "8px 14px", fontSize: 11.5, fontWeight: 600,
              color: "#fff", cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
